class LoadMore extends HTMLElement {
    constructor() {
        super();
        this.handleClick = this.handleClick.bind(this);
        this.isLoading = false;
        this.productItem = null;
    }

    /**
     * Sets up the load more button when the element is added to the page.
     */
    connectedCallback() {
        this.syncProperties();
        this.button = this.querySelector('.js-load-more');
        this.button?.addEventListener('click', this.handleClick);
        this.prepareCarriedProductItem();
        this.updateItemPerPageDisplay();

        if (this.isLastPage()) {
            this.showProductsFinished();
        }
    }

    /**
     * Removes listeners when this instance is replaced by the next pagination control.
     */
    disconnectedCallback() {
        this.button?.removeEventListener('click', this.handleClick);
    }

    /**
     * Reads configuration from data attributes.
     */
    syncProperties() {
        this.nextUrl = this.dataset.next;
        this.currentPage = Number.parseInt(this.dataset.current, 10) || 1;
        this.totalPages = Number.parseInt(this.dataset.pages, 10) || 1;
        this.wrapperClass = this.dataset.wrapperClass;
        this.subWrapperClass = this.dataset.subWrapperClass;
        this.gridItemClass = this.dataset.gridItemClass;
        this.totalItems = this.dataset.itemNumber;
        this.showItemPerPage = this.dataset.showItemPerPage ?? 'true';
        this.itemPerPage = 0;
        this.hasImage = this.dataset.image === 'true';
    }

    /**
     * Handles load more button clicks.
     */
    handleClick(event) {
        event.preventDefault();
        this.loadProducts();
    }

    /**
     * Fetches the next page and appends its items to the current grid.
     */
    async loadProducts() {
        if (this.isLoading) {
            return;
        }

        if (this.isLastPage() || !this.nextUrl) {
            this.showProductsFinished();
            return;
        }

        this.setLoadingState(true);

        try {
            const response = await fetch(this.nextUrl);

            if (!response.ok) {
                throw new Error(`Failed to load products: ${response.status}`);
            }

            const html = await response.text();
            this.processLoadedContent(html);
        } catch (error) {
            console.warn('Something went wrong loading more products.', error);
        } finally {
            this.setLoadingState(false);
        }
    }

    /**
     * Returns true when there are no more paginated pages to fetch.
     *
     * @returns {boolean}
     */
    isLastPage() {
        return this.currentPage >= this.totalPages;
    }

    /**
     * Parses fetched HTML and appends the new grid items.
     *
     * @param {string} html
     */
    processLoadedContent(html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newGrid = this.findGrid(doc);
        const newLoadMore = this.findLoadMore(doc);
        const currentGrid = this.getCurrentGrid();

        if (!newGrid || !newLoadMore || !currentGrid) {
            return;
        }

        this.insertCarriedProductItem(newGrid);
        const firstNewItem = this.appendNewItems(newGrid, currentGrid);

        this.updateNextControl(newLoadMore);
        this.replaceWith(newLoadMore);
        this.focusFirstNewItem(firstNewItem);
    }

    /**
     * Moves the last visible product forward when an editorial image tile affects page size.
     */
    prepareCarriedProductItem() {
        const currentGrid = this.getCurrentGrid();

        if (!this.hasImage || this.isLastPage() || !currentGrid) {
            this.itemPerPage = this.countVisibleItems(currentGrid);
            return;
        }

        this.productItem = currentGrid.lastElementChild;

        if (this.productItem) {
            currentGrid.removeChild(this.productItem);
        }

        this.itemPerPage = this.countVisibleItems(currentGrid);
    }

    /**
     * Inserts a product carried over from the previous page into the fetched grid.
     *
     * @param {Element} newGrid
     */
    insertCarriedProductItem(newGrid) {
        if (!this.hasImage || !this.productItem) {
            return;
        }

        this.productItem.style.setProperty('--animation-order', '1');
        newGrid.insertBefore(this.productItem, newGrid.firstElementChild);
    }

    /**
     * Appends fetched items to the current grid.
     *
     * @param {Element} newGrid
     * @param {Element} currentGrid
     * @returns {Element|null}
     */
    appendNewItems(newGrid, currentGrid) {
        const newItems = Array.from(newGrid.querySelectorAll(`.${this.gridItemClass}`));
        const firstNewItem = newItems[0] ?? null;

        newItems.forEach((newItem) => {
            currentGrid.appendChild(newItem);
        });

        return firstNewItem;
    }

    /**
     * Updates the next pagination control before it replaces the current one.
     *
     * @param {Element} newLoadMore
     */
    updateNextControl(newLoadMore) {
        const nextCurrentPage = Number.parseInt(newLoadMore.dataset.current, 10) || this.currentPage + 1;
        const nextTotalPages = Number.parseInt(newLoadMore.dataset.pages, 10) || this.totalPages;

        if (nextCurrentPage >= nextTotalPages || !newLoadMore.dataset.next) {
            this.disableControl(newLoadMore);
        }
    }

    /**
     * Shows the finished state on this control.
     */
    showProductsFinished() {
        this.disableControl(this);
    }

    /**
     * Disables a load more control and updates its label.
     *
     * @param {Element} control
     */
    disableControl(control) {
        const textElement = control.querySelector('[load-more-text]');
        const button = control.querySelector('.js-load-more');

        if (textElement) {
            textElement.innerText = 'Products Finished';
            textElement.classList.remove('hide');
        }

        if (button) {
            button.classList.add('tw-hidden');
            button.setAttribute('disabled', 'true');
        }
    }

    /**
     * Toggles loading styles and button state.
     *
     * @param {boolean} isLoading
     */
    setLoadingState(isLoading) {
        this.isLoading = isLoading;
        this.getWrapper()?.classList.toggle('loading', isLoading);
        this.button?.toggleAttribute('disabled', isLoading);

        if (isLoading) {
            this.querySelector('[load-more-text]')?.classList.add('hide');
        } else {
            this.querySelector('[load-more-text]')?.classList.remove('hide');
        }
    }

    /**
     * Updates the visible item count.
     */
    updateItemPerPageDisplay() {
        this.dataset.itemPerPage = this.itemPerPage;

        if (this.showItemPerPage !== 'true') {
            return;
        }

        const itemPerPageElement = this.querySelector('.item_per_page');

        if (itemPerPageElement) {
            itemPerPageElement.innerText = this.itemPerPage;
        }
    }

    /**
     * Counts grid items that are not editorial image tiles.
     *
     * @param {Element} grid
     * @returns {number}
     */
    countVisibleItems(grid) {
        return Array.from(grid.children).filter((child) => !child.classList.contains('grid__item-image')).length;
    }

    /**
     * Finds the product/article grid in a document.
     *
     * @param {Document|Element} root
     * @returns {Element|null}
     */
    findGrid(root) {
        return root.querySelector(`.${this.wrapperClass} .${this.subWrapperClass}`);
    }

    /**
     * Finds the next load more element in fetched markup.
     *
     * @param {Document} doc
     * @returns {Element|null}
     */
    findLoadMore(doc) {
        return doc.querySelector(`.${this.wrapperClass} load-more`) ?? doc.querySelector('load-more');
    }

    /**
     * Moves focus to the first newly-added item for keyboard and screen reader users.
     *
     * @param {Element|null} firstNewItem
     */
    focusFirstNewItem(firstNewItem) {
        if (!firstNewItem) {
            return;
        }

        const firstFocusable = this.findFirstFocusableElement(firstNewItem);

        if (firstFocusable) {
            this.focusElement(firstFocusable);
            return;
        }

        this.focusItemTemporarily(firstNewItem);
    }

    /**
     * Finds the first focusable child inside an item.
     *
     * @param {Element} element
     * @returns {Element|null}
     */
    findFirstFocusableElement(element) {
        const focusableSelectors = [
            'a[href]',
            'button:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex="0"]'
        ].join(', ');

        return element.querySelector(focusableSelectors);
    }

    /**
     * Focuses and scrolls an element into view after the DOM updates.
     *
     * @param {Element} element
     */
    focusElement(element) {
        setTimeout(() => {
            element.focus();
            element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 150);
    }

    /**
     * Temporarily makes an item focusable when it has no focusable children.
     *
     * @param {Element} item
     */
    focusItemTemporarily(item) {
        item.setAttribute('tabindex', '-1');

        setTimeout(() => {
            item.focus();
            item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

            setTimeout(() => {
                item.removeAttribute('tabindex');
            }, 1000);
        }, 150);
    }

    /**
     * Returns the wrapper that contains the grid and load more control.
     *
     * @returns {Element|null}
     */
    getWrapper() {
        return document.querySelector(`.${this.wrapperClass}`);
    }

    /**
     * Returns the current grid that receives new items.
     *
     * @returns {Element|null}
     */
    getCurrentGrid() {
        return this.findGrid(document);
    }
}

if (!customElements.get('load-more')) {
    customElements.define('load-more', LoadMore);
}
